import { useState, useEffect, useMemo } from 'react'; // eslint-disable-line @typescript-eslint/no-unused-vars
import { Link } from 'react-router-dom';
import { useCredits } from '../hooks/useCredits';
import { useAuth } from '../hooks/useAuth';
import { safeInvoke } from '../lib/safeInvoke';
import { supabase } from '../lib/supabase';
import { TraitSelector, type TraitSelections } from '../components/astromatch/TraitSelector';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';
import { ReadingActions } from '../components/shared/ReadingActions';
import { PhaseLoader } from '../components/shared/PhaseLoader';
import { useNavigate } from 'react-router-dom';
import { haptics } from '../lib/native/haptics';
import { useI18n } from '../hooks/useI18n';
import { usePricing } from '../contexts/PricingContext';
import { useAvulsoProduct } from '../hooks/useAvulsoProducts';
import { AvulsoCompareModal } from '../components/shared/AvulsoCompareModal';
import { Eye, Trash2 } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────
interface AstroMatchResult {
  score: number;
  summary: string;
  verdict: string;
  numerology: string;
  physiognomy: string;
  readingId: string | null;
}

interface AMHistoryItem {
  id: string;
  subject_name: string | null;
  score: number | null;
  summary: string | null;
  verdict: string | null;
  numerology: string | null;
  physiognomy: string | null;
  eye_color: string | null;
  face_shape: string | null;
  nose_type: string | null;
  lips_type: string | null;
  hair_type: string | null;
  hair_color: string | null;
  height_category: string | null;
  energy_type: string | null;
  created_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────
function scoreColor(s: number) {
  return s >= 75 ? '#4ade80' : s >= 50 ? '#F5C842' : s >= 30 ? '#fb923c' : '#f87171';
}

const ASTROMATCH_PHASES = [
  'Consultando os astros...',
  'Cruzando seu mapa natal com o perfil...',
  'Analisando compatibilidade numerológica...',
  'Calculando sintonia energética...',
];

// ─── Score ring ───────────────────────────────────────────────────────
function ScoreRing({ score }: { score: number }) {
  const r = 52, circ = 2 * Math.PI * r;
  const pct = score / 100;
  const color = scoreColor(score);
  const label = score >= 80 ? 'Conexão extraordinária ✦' : score >= 60 ? 'Boa sintonia energética' : score >= 40 ? 'Energia complementar' : 'Desafio transformador';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
      <svg width="130" height="130" viewBox="0 0 130 130">
        <circle cx="65" cy="65" r={r} fill="none" stroke="var(--border-subtle)" strokeWidth="10" />
        <circle cx="65" cy="65" r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
          strokeLinecap="round" transform="rotate(-90 65 65)"
          style={{ transition: 'stroke-dashoffset 1s ease' }} />
        <text x="65" y="60" textAnchor="middle" fontSize="22" fontWeight="700" fill="var(--text-primary)" fontFamily="sans-serif">{score}</text>
        <text x="65" y="78" textAnchor="middle" fontSize="10" fill="var(--text-muted)" fontFamily="sans-serif">/ 100</text>
      </svg>
      <p style={{ fontSize: 'var(--text-label)', color, fontWeight: 600, textAlign: 'center' }}>{label}</p>
    </div>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────────
const TABS = [
  { key: 'summary',     label: '💘 Resumo' },
  { key: 'verdict',     label: '⭐ Veredito' },
  { key: 'numerology',  label: '🔢 Numerologia' },
  { key: 'physiognomy', label: '👁️ Fisionomia' },
];

// ─── Main page ────────────────────────────────────────────────────────
export function AstroMatchPage() {
  const { profile }                            = useAuth();
  const { credits, isUnlimited, refresh, creditsError } = useCredits();
  const navigate                               = useNavigate();
  const { t, lang }                            = useI18n();
  const { getProduct }                         = usePricing();
  const avulsoProduct                          = useAvulsoProduct('astromatch');

  const [consent,       setConsent]     = useState(false);
  const [subjectName,   setSubjectName] = useState('');
  const [traits,        setTraits]      = useState<TraitSelections>({});
  const [loading,       setLoading]     = useState(false);
  const [result,        setResult]      = useState<AstroMatchResult | null>(null);
  const [error,         setError]       = useState<string | null>(null);
  const [activeTab,     setActiveTab]   = useState('summary');

  const [history,       setHistory]     = useState<AMHistoryItem[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showForm,      setShowForm]    = useState(false);
  const [showAvulsoModal, setShowAvulsoModal] = useState(false);

  useEffect(() => {
    if (result) { setResult(null); }
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load history
  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from('astromatch_readings')
      .select('id, subject_name, score, summary, verdict, numerology, physiognomy, eye_color, face_shape, nose_type, lips_type, hair_type, hair_color, height_category, energy_type, created_at')
      .eq('user_id', profile.id)
      .not('summary', 'is', null)
      .order('created_at', { ascending: false })
      .limit(30)
      .then(({ data }) => {
        if (data) setHistory(data as AMHistoryItem[]);
        setHistoryLoaded(true);
      });
  }, [profile?.id, result]);

  // Deduplicate, one card per subject_name (most recent)
  const uniqueHistory = useMemo(() => {
    const seen = new Set<string>();
    return history.filter(h => {
      const key = h.subject_name?.trim().toLowerCase() ?? `__anon_${h.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [history]);

  function loadFromHistory(item: AMHistoryItem) {
    if (!item.summary) return;
    setSubjectName(item.subject_name ?? '');
    setTraits({
      eye_color:       item.eye_color       ?? undefined,
      face_shape:      item.face_shape      ?? undefined,
      nose_type:       item.nose_type       ?? undefined,
      lips_type:       item.lips_type       ?? undefined,
      hair_type:       item.hair_type       ?? undefined,
      hair_color:      item.hair_color      ?? undefined,
      height_category: item.height_category ?? undefined,
      energy_type:     item.energy_type     ?? undefined,
    });
    setResult({
      score:       item.score       ?? 0,
      summary:     item.summary     ?? '',
      verdict:     item.verdict     ?? '',
      numerology:  item.numerology  ?? '',
      physiognomy: item.physiognomy ?? '',
      readingId:   item.id,
    });
    setActiveTab('summary');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function deleteReading(item: AMHistoryItem) {
    await supabase
      .from('astromatch_readings')
      .delete()
      .eq('user_id', profile!.id)
      .eq('subject_name', item.subject_name);
    setHistory(prev => prev.filter(h => h.subject_name !== item.subject_name));
  }

  async function handleAnalyze() {
    if (!consent) return;

    // Cache check, if subject_name provided and prior reading exists, load free
    const isAdmin = profile?.role === 'admin';
    if (subjectName.trim() && profile?.id && !isAdmin) {
      const { data: cached } = await supabase
        .from('astromatch_readings')
        .select('id, score, summary, verdict, numerology, physiognomy, eye_color, face_shape, nose_type, lips_type, hair_type, hair_color, height_category, energy_type')
        .eq('user_id', profile.id)
        .ilike('subject_name', subjectName.trim())
        .not('summary', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cached?.summary) {
        await haptics.heavy();
        setResult({
          score:       cached.score       ?? 0,
          summary:     cached.summary     ?? '',
          verdict:     cached.verdict     ?? '',
          numerology:  cached.numerology  ?? '',
          physiognomy: cached.physiognomy ?? '',
          readingId:   cached.id,
        });
        setActiveTab('summary');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setResult(null);

    const consentTimestamp = new Date().toISOString();

    const { data, fromFallback } = await safeInvoke<AstroMatchResult>('astromatch-analyze', {
      body: {
        traits,
        subjectName:      subjectName || undefined,
        consentConfirmed: true,
        consentTimestamp,
      },
      fallback: { score: 0, summary: '', verdict: '', numerology: '', physiognomy: '', readingId: null },
      timeout:  30_000,
    });

    setLoading(false);
    if (fromFallback || !data.summary) {
      setError(t('pages.astromatch.errorMsg'));
      return;
    }
    await haptics.heavy();
    setResult(data);
    setActiveTab('summary');
    refresh();
  }

  if (creditsError) return (
    <div className="page-container" style={{ textAlign: 'center', paddingTop: '3rem' }}>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>Erro ao carregar créditos.</p>
      <button className="btn btn-secondary btn-sm" onClick={refresh}>Tentar novamente</button>
    </div>
  );

  const noCredits = !isUnlimited && credits.astromatch <= 0;
  const tabContent: Record<string, string> = result ? {
    summary:     result.summary,
    verdict:     result.verdict,
    numerology:  result.numerology,
    physiognomy: result.physiognomy,
  } : {};

  return (
    <div className="page-container">

      {/* ── Header ──────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', gap: '1rem' }}>
        <div>
          <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
            {t('pages.astromatch.title')}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
            Análise de compatibilidade entre o seu mapa astral e a análise numerológica e fisionômica do seu crush.
            Preencha as informações abaixo, todos os campos são opcionais, mas quanto mais detalhes, mais precisa a análise. ✦
          </p>
          <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span className="credit-pill">🔮 {isUnlimited ? '∞' : credits.astromatch} crédito{!isUnlimited && credits.astromatch === 1 ? '' : 's'}</span>
            {noCredits && <button className="btn btn-primary btn-sm" onClick={() => navigate('/wallet')}>Comprar créditos ✦</button>}
            {noCredits && avulsoProduct && (
              <button className="btn btn-ghost btn-sm" onClick={() => setShowAvulsoModal(true)} style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', borderColor: 'rgba(31,78,95,0.4)' }}>
                Comprar avulso R${avulsoProduct.price_brl.toFixed(2).replace('.', ',')}
              </button>
            )}
          </div>
        </div>
        {!result && historyLoaded && uniqueHistory.length > 0 && !showForm && (
          <button className="btn btn-primary btn-sm" style={{ flexShrink: 0, whiteSpace: 'nowrap', borderRadius: '999px', padding: '0.5rem 1.125rem' }}
            onClick={() => setShowForm(true)}>
            + Nova Análise
          </button>
        )}
      </div>

      {/* ── No result view ────────────────────────────────── */}
      {!result && !loading && (
        <>
          {/* ── Perfis Analisados ────────────────────────── */}
          {historyLoaded && uniqueHistory.length > 0 && !showForm && (
            <div className="card" style={{ marginBottom: '1.5rem', padding: '1.25rem' }}>
              <h2 className="font-display" style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>
                Perfis Analisados
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {uniqueHistory.map((item, idx) => {
                  const sc      = item.score ?? 0;
                  const initial = (item.subject_name ?? '?')[0].toUpperCase();
                  return (
                    <div key={item.id} style={{
                      display: 'flex', alignItems: 'center', gap: '0.875rem',
                      padding: '0.875rem 0',
                      borderTop: idx > 0 ? '1px solid var(--border-default)' : 'none',
                      cursor: 'pointer',
                    }}
                      onClick={() => loadFromHistory(item)}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: '2.5rem', height: '2.5rem', borderRadius: '50%', flexShrink: 0,
                        background: 'rgba(31,78,95,0.2)', border: '1px solid rgba(31,78,95,0.35)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1rem', fontWeight: 700, color: 'var(--petrol)',
                      }}>{initial}</div>
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.25rem', fontSize: 'var(--text-label)' }}>
                          {item.subject_name ?? 'Perfil anônimo'}
                        </p>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: scoreColor(sc) }}>{sc}%</span>
                      </div>
                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                        <span style={{ padding: '0.35rem', color: 'var(--text-muted)' }}>
                          <Eye size={18} />
                        </span>
                        <button title="Excluir" onClick={e => { e.stopPropagation(); deleteReading(item); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.35rem', color: '#f87171' }}>
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Form (shown when history is empty OR showForm=true) ── */}
          {(showForm || !historyLoaded || uniqueHistory.length === 0) && (
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              {/* Subject name */}
              <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                <label className="form-label" htmlFor="subject-name">
                  {t('pages.astromatch.subjectNameLabel')}
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> {t('pages.astromatch.subjectNameOptional')}</span>
                </label>
                <input
                  id="subject-name"
                  type="text"
                  className="form-input"
                  value={subjectName}
                  onChange={e => setSubjectName(e.target.value)}
                  placeholder={t('pages.astromatch.subjectNamePlaceholder')}
                />
              </div>

              {/* Trait selector */}
              <TraitSelector value={traits} onChange={setTraits} />

              {/* Consent checkbox */}
              <label style={{
                display:    'flex',
                alignItems: 'flex-start',
                gap:        '0.6rem',
                marginTop:  '1.5rem',
                cursor:     'pointer',
                fontSize:   'var(--text-caption)',
                color:      'var(--text-secondary)',
                lineHeight: 1.5,
              }}>
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                  style={{ marginTop: '2px', flexShrink: 0, accentColor: 'var(--color-gold)' }}
                />
                <span>
                  ✓ Confirmo que as informações são para análise pessoal e de entretenimento
                </span>
              </label>

              {/* Analyze button */}
              <button
                className="btn btn-primary"
                disabled={!consent || noCredits}
                onClick={handleAnalyze}
                style={{ width: '100%', marginTop: '1rem', justifyContent: 'center' }}
              >
                🔮 Analisar compatibilidade
              </button>

              {!consent && (
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.75rem' }}>
                  {t('pages.astromatch.noConsent')}
                </p>
              )}

              {showForm && (
                <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem', width: '100%' }}
                  onClick={() => { setShowForm(false); setSubjectName(''); setTraits({}); setConsent(false); }}>
                  Cancelar
                </button>
              )}
            </div>
          )}
        </>
      )}

      {loading && <PhaseLoader phases={ASTROMATCH_PHASES} />}

      {error && !loading && (
        <div style={{ padding: '1rem', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-md)', color: '#fca5a5', fontSize: 'var(--text-label)', marginBottom: '1.5rem' }}>
          {error}
          <br />
          <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }} onClick={() => setError(null)}>
            {t('pages.astromatch.errorRetry')}
          </button>
        </div>
      )}

      {result && !loading && (
        <div className="animate-fade-in">
          {/* Score */}
          <div className="card" style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <ScoreRing score={result.score} />
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.35rem', overflowX: 'auto', marginBottom: '1rem', paddingBottom: '0.25rem' }}>
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={activeTab === tab.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="card animate-fade-in" key={activeTab}>
            <div className="reading-text">
              {(tabContent[activeTab] || '').split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <ReadingActions pageName="AstroMatch" reading={result.summary} signEmoji="🔮" />
            <button className="btn btn-secondary btn-sm" onClick={() => { setResult(null); setError(null); setTraits({}); setSubjectName(''); setConsent(false); setShowForm(false); }}>
              {t('pages.astromatch.newAnalysis')}
            </button>
          </div>
          <ReadingFeedback feature="astromatch-analyze" readingId={result.readingId ?? undefined} />

          {/* Sinastria cross-sell */}
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem 1.125rem',
            borderRadius: 'var(--radius-card)',
            border: '1px solid rgba(96,165,250,0.3)',
            background: 'linear-gradient(135deg, rgba(96,165,250,0.07) 0%, transparent 100%)',
          }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 0.35rem' }}>
              💫 Aprofunde ainda mais
            </p>
            <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', margin: '0 0 0.75rem', lineHeight: 1.55 }}>
              O AstroMatch revela a compatibilidade básica entre vocês. A <strong style={{ color: 'var(--text-primary)' }}>Sinastria completa</strong> vai muito além, ela analisa a dinâmica real do relacionamento, os desafios, os pontos de crescimento e o potencial de longo prazo de vocês dois.
            </p>
            <Link to="/synastry" className="btn btn-sm" style={{ textDecoration: 'none', background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.3)' }}>
              Ver Sinastria completa →
            </Link>
          </div>
        </div>
      )}
      {showAvulsoModal && avulsoProduct && (
        <AvulsoCompareModal
          serviceName="AstroMatch"
          serviceSlug="astromatch"
          avulsoPrice={avulsoProduct.price_brl}
          avulsoDesc={avulsoProduct.description}
          monthlyProd={getProduct('monthly_sub')}
          annualProd={getProduct('annual_sub')}
          onClose={() => setShowAvulsoModal(false)}
        />
      )}
    </div>
  );
}
